import { useEffect, useRef, useState } from "react";
import "./style.css"
import { Helmet } from "react-helmet-async";
import { Chat, MessageChat } from "../../types";
import { useNavigate, useSearchParams } from "react-router-dom";
import Screen from "../Layouts/Screen/Screen";
import ChatListComponent from "../../components/chat-list";
import { Bell, BellOff, Inbox, Trash2, UserMinus, X } from "lucide-react";
import CreateGroupModal from "../../components/Modals/CreateGroupModal";
import NewConversationPanel from "../../components/NewConversationPanel";
import { useAuth, useMessage } from "../../global-components/src";
import { api } from "../../services/api";
import { PACOCA_API_URL } from "../../global-components/src/utils/vars";
import { useChatEcho } from "../../hooks/useChatEcho";
import { getChatListCacheKey, invalidateChatListCache } from "../../utils/chat-list-cache";
import { useTranslation } from "react-i18next";

const parseChatListMessage = (value: unknown): MessageChat | null => {
    if (!value) return null;
    if (typeof value !== "string") return value as MessageChat;
    try {
        return JSON.parse(value) as MessageChat;
    } catch {
        return null;
    }
};

const ChatList = () => {
    const [searchParams] = useSearchParams();
    const { t } = useTranslation();
    const [loadingTermo, setLoadingTermo] = useState(false);
    const [loading, setLoading] = useState(false);
    const [termo, setTermo] = useState(searchParams.get("termo") || "");
    const navigate = useNavigate();
    const [isDropdownVisible, setDropdownVisible] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showCreateGroup, setShowCreateGroup] = useState(false);
    const [showNewConversation, setShowNewConversation] = useState(false);
    const [dataNotFound, setDataNotFound] = useState(false);
    const [selectedMessages, setSelectedMessages] = useState<number[]>([]);
    const [members, setMembers] = useState<Chat[] | null>(null);
    const [isMovingSelected, setIsMovingSelected] = useState(false);
    const { token, user } = useAuth();
    const { setMessage } = useMessage();

    // Keep a ref so the WS callback always sees the latest userId without re-subscribing
    const userIdRef = useRef(user?.id);
    useEffect(() => { userIdRef.current = user?.id; }, [user?.id]);

    // Track current tab so the WS handler never overwrites the principal cache
    // when the user is on the "pending" (Solicitações) tab.
    const notificationsTypeRef = useRef<string>("principal");

    // ── Real-time chat list updates via WebSocket (Laravel Reverb) ────────────
    // When a new message arrives for any chat, bump its preview to the top and
    // show the unread badge instantly — no polling needed.
    useChatEcho({
        userId: user?.id,
        token,
        onMessage: (chatId, message) => {
            setMembers(prev => {
                if (!prev) return prev;

                const idx = prev.findIndex(m => Number(m.chat_id) === Number(chatId));
                if (idx === -1) return prev; // unknown chat — ignore

                const serialized = JSON.stringify(message);
                const updated: Chat[] = [...prev];

                // Move the updated chat to the top with the new last_message
                const [chat] = updated.splice(idx, 1);
                updated.unshift({ ...chat, last_message: serialized as any });

                // Only persist the principal tab cache — never overwrite it with pending chats
                if (notificationsTypeRef.current === "principal") {
                    try {
                        localStorage.setItem(getChatListCacheKey(userIdRef.current), JSON.stringify(updated));
                    } catch { /* quota exceeded — ignore */ }
                }

                return updated;
            });
        },
        onRead: ({ chat_id, reader_id, message_id, read_at, is_read, read_by_count, read_by_total }) => {
            const currentUserId = userIdRef.current;
            if (!currentUserId || Number(reader_id) === Number(currentUserId)) return;

            setMembers(prev => {
                if (!prev) return prev;

                const updated = prev.map(member => {
                    if (Number(member.chat_id) !== Number(chat_id)) return member;

                    const lastMessage = parseChatListMessage(member.last_message);
                    if (
                        !lastMessage
                        || lastMessage.message_type === "system"
                        || Number(lastMessage.user_id) !== Number(currentUserId)
                        || Number(lastMessage.id) > Number(message_id)
                    ) {
                        return member;
                    }

                    const isGroup = Boolean(member.is_group);
                    const isSameMessage = Number(lastMessage.id) === Number(message_id);
                    const hasReadCounts = Number.isFinite(Number(read_by_count))
                        && Number.isFinite(Number(read_by_total));
                    const allMembersRead = is_read === true;
                    const nextLastMessage = {
                        ...lastMessage,
                        // A group only becomes read when the server confirms
                        // that every eligible member has seen the message.
                        is_read: isGroup
                            ? allMembersRead || (!isSameMessage ? lastMessage.is_read : false)
                            : is_read ?? true,
                        ...(isGroup && hasReadCounts && (isSameMessage || allMembersRead)
                            ? {
                                read_by_count: allMembersRead ? read_by_total : read_by_count,
                                read_by_total,
                            }
                            : {}),
                        ...(read_at ? { read_at } : {}),
                    };

                    return {
                        ...member,
                        last_message: nextLastMessage,
                    };
                });

                if (notificationsTypeRef.current === "principal") {
                    try {
                        localStorage.setItem(getChatListCacheKey(currentUserId), JSON.stringify(updated));
                    } catch { /* quota exceeded — ignore */ }
                }

                return updated;
            });
        },
    });

    useEffect(() => {
        const handleClickOutside = () => {
            if (isDropdownVisible) setDropdownVisible(false);
        };
        window.addEventListener('click', handleClickOutside);
        return () => window.removeEventListener('click', handleClickOutside);
    }, [isDropdownVisible]);

    useEffect(() => {
        setLoadingTermo(true);
    }, [termo]);

    // Returns true if all selected chats are currently muted
    const allSelectedMuted = (): boolean => {
        if (!members || !selectedMessages.length) return false;
        return selectedMessages.every(id => members.find(m => m.chat_id === id)?.is_muted);
    };

    const handleToggleMute = async () => {
        if (!members || !selectedMessages.length) return;

        const targetMuted = !allSelectedMuted();

        // Optimistic update
        setMembers(prev =>
            prev ? prev.map(m =>
                selectedMessages.includes(m.chat_id) ? { ...m, is_muted: targetMuted } : m
            ) : prev
        );

        setSelectedMessages([]);

        // Fire API calls (fire-and-forget; backend toggles so we call once per chat)
        for (const chatId of selectedMessages) {
            try {
                await api(token).patch(`${PACOCA_API_URL}/chats/${chatId}/mute`);
            } catch (e) {
                // revert on error
                setMembers(prev =>
                    prev ? prev.map(m =>
                        m.chat_id === chatId ? { ...m, is_muted: !targetMuted } : m
                    ) : prev
                );
            }
        }
    };

    const selectedChats = (members ?? []).filter(chat => selectedMessages.includes(chat.chat_id));
    const targetInbox = notificationsTypeRef.current === 'principal'
        ? 'general'
        : notificationsTypeRef.current === 'general'
            ? 'principal'
            : null;
    const canMoveSelected = Boolean(
        targetInbox
        && selectedChats.length === selectedMessages.length
        && selectedChats.every(chat =>
            !chat.is_group
            && chat.direct_request_state !== 'pending'
            && chat.direct_request_state !== 'rejected'
        )
    );

    const handleMoveSelected = async () => {
        if (!targetInbox || !canMoveSelected || isMovingSelected) return;

        const selectedIds = selectedChats.map(chat => chat.chat_id);
        const movedIds: number[] = [];
        setIsMovingSelected(true);

        for (const chatId of selectedIds) {
            try {
                await api(token).put(`${PACOCA_API_URL}/chats/${chatId}/inbox`, { inbox: targetInbox });
                movedIds.push(chatId);
            } catch {
                setMessage(t('chat.messageRequests.moveFailed'), 'error');
            }
        }

        if (movedIds.length) {
            invalidateChatListCache(user?.id);
            setMembers(prev => prev ? prev.filter(chat => !movedIds.includes(chat.chat_id)) : prev);
        }

        setSelectedMessages(selectedIds.filter(chatId => !movedIds.includes(chatId)));
        setIsMovingSelected(false);
    };

    return (
        <>
            <CreateGroupModal
                show={showCreateGroup}
                onClose={() => setShowCreateGroup(false)}
                onCreated={(chatId) => {
                    invalidateChatListCache(user?.id);
                    setShowCreateGroup(false);
                    navigate(`/chat/${chatId}`);
                }}
            />
            <NewConversationPanel
                show={showNewConversation}
                onClose={() => setShowNewConversation(false)}
                onNewGroup={() => setShowCreateGroup(true)}
            />
            <Screen title={t("chat.conversations")} showMenuIcon={!selectedMessages?.length} contentMenu={
                <>
                    {selectedMessages?.length ? (
                        <>
                            {selectedMessages.length > 1 && (
                                <span style={{ fontSize: 13, opacity: 0.7, marginRight: 2, alignSelf: "center" }}>
                                    {t("chat.selected_other", { count: selectedMessages.length })}
                                </span>
                            )}
                            {canMoveSelected && targetInbox && (
                                <button
                                    className="icon-small-menu-top"
                                    onClick={handleMoveSelected}
                                    disabled={isMovingSelected}
                                    aria-label={t(targetInbox === 'general' ? "chat.messageRequests.moveToGeneral" : "chat.messageRequests.moveToPrincipal")}
                                    title={t(targetInbox === 'general' ? "chat.messageRequests.moveToGeneral" : "chat.messageRequests.moveToPrincipal")}
                                    style={{ border: 0, borderRadius: "5px", background: "transparent", marginTop: -3, marginRight: 0, opacity: isMovingSelected ? 0.5 : 1 }}
                                    type="button"
                                >
                                    <Inbox opacity={.6} size={20}/>
                                </button>
                            )}
                            <button
                                className="icon-small-menu-top"
                                onClick={handleToggleMute}
                                aria-label={allSelectedMuted() ? t("chat.unmuteNotifications") : t("settings.muteConversation")}
                                title={allSelectedMuted() ? t("chat.unmuteNotifications") : t("chat.muteNotifications")}
                                style={{ border: 0, borderRadius: "5px", background: "transparent", marginTop: -3, marginRight: 0 }}
                                type="button"
                            >
                                {allSelectedMuted() ? <Bell opacity={.6} size={20}/> : <BellOff opacity={.6} size={20}/>}
                            </button>
                            {selectedMessages.every(id => !members?.find(m => m.chat_id === id)?.is_group) && (
                                <button
                                    className="icon-small-menu-top"
                                    onClick={() => setShowConfirmation(true)}
                                    aria-label={t("chat.deleteConversation")}
                                    style={{ border: 0, borderRadius: "5px", background: "transparent", marginTop: -3, marginRight: -10 }}
                                    type="button"
                                >
                                    <Trash2 opacity={.6} size={20}/>
                                </button>
                            )}
                            <button
                                className="icon-small-menu-top"
                                onClick={() => setSelectedMessages([])}
                                aria-label={t("chat.cancelSelection")}
                                style={{ border: 0, borderRadius: "5px", background: "transparent", marginTop: -3 }}
                                type="button"
                            >
                                <X opacity={.6} size={23}/>
                            </button>
                        </>
                    ) : (
                        <button
                            className="icon-small-menu-top"
                            title={t("chat.newConversation")}
                            type="button"
                            onClick={() => setShowNewConversation(true)}
                            style={{ border: 0, borderRadius: "5px", background: "transparent", marginTop: -3, marginRight: -10 }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity={0.6}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        </button>
                    )}
                </>
            }>
                <Helmet>
                    <title>Paçoca - Chat</title>
                </Helmet>

                <>
                    <div className="container-search">
                        <ChatListComponent members={members} setMembers={setMembers} setShowConfirmation={setShowConfirmation} showConfirmation={showConfirmation} selectedMessages={selectedMessages} setSelectedMessages={setSelectedMessages} onNotificationsTypeChange={(type) => { notificationsTypeRef.current = type; }}/>
                    </div>

                    {dataNotFound && !loading ? (
                        <div className="user-select-none row row-empty-comment">
                            <span className="span-empty">
                                <UserMinus className="icon-empty" />
                                <h3 className="w-100 text-center">{t("chat.noConversations")}</h3>
                            </span>
                        </div>
                    ) : null}
                </>
            </Screen>
        </>
    );
};

export default ChatList;
